import { useEffect, useState } from 'react';
import { Alert } from 'react-native';
import { isDetoxTestControlsEnabled } from '../config/environments';
import { reviewService } from '../services/reviewService';
import {
  buildInitialShareMappingState,
  buildNewQuestionMapping,
  getActiveQuestionFields,
  getAllItems,
  getCompatibleQuestionTargets,
  updateQuestionMapping,
  type QuestionField,
  type QuestionMapping,
  type ReviewCategory,
  type ReviewItem,
  type ReviewTemplate
} from './shareItemMapping';
import {
  createMetadataOnlyShareLink,
  createShareLink,
  createShareTargetItem,
  getShareActionErrorMessage
} from './shareItemActions';

interface UseShareItemModalStateParams {
  sourceItem: ReviewItem;
  sourceTemplateId: number;
  templates: ReviewTemplate[];
  onDismiss: () => void;
  onSuccess: () => void;
}

export const useShareItemModalState = ({
  sourceItem,
  sourceTemplateId,
  templates,
  onDismiss,
  onSuccess
}: UseShareItemModalStateParams) => {
  const [targetTemplateId, setTargetTemplateId] = useState<number | null>(null);
  const [targetCategoryId, setTargetCategoryId] = useState<number | null>(null);
  const [targetItemId, setTargetItemId] = useState<number | null>(null);
  const [questionMappings, setQuestionMappings] = useState<QuestionMapping[]>([]);
  const [questionsToCopy, setQuestionsToCopy] = useState<Set<string>>(new Set());
  const [includeRoom, setIncludeRoom] = useState(true);
  const [includePhotos, setIncludePhotos] = useState(true);
  const [includeFloor, setIncludeFloor] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [fullTemplate, setFullTemplate] = useState<ReviewTemplate | null>(null);

  const [createItemMode, setCreateItemMode] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [creatingItem, setCreatingItem] = useState(false);

  const [templateMenuVisible, setTemplateMenuVisible] = useState(false);
  const [categoryMenuVisible, setCategoryMenuVisible] = useState(false);
  const [itemMenuVisible, setItemMenuVisible] = useState(false);
  const [sourceMappingMenuId, setSourceMappingMenuId] = useState<string | null>(null);
  const [targetMappingMenuId, setTargetMappingMenuId] = useState<string | null>(null);

  const availableTemplates = templates.filter(t => t.id !== sourceTemplateId);
  const firstAvailableTemplate = availableTemplates[0];
  const targetCategories = fullTemplate?.categories || [];
  const selectedCategory = targetCategories.find(c => c.id === targetCategoryId);
  const targetItems = selectedCategory ? getAllItems(selectedCategory) : [];
  const targetItem = targetItems.find(i => i.id === targetItemId);
  const isNewItem = Boolean(targetItem && (!targetItem.questionsSchema?.fields || targetItem.questionsSchema.fields.length === 0));
  const sourceFields = getActiveQuestionFields(sourceItem);
  const targetFields = getActiveQuestionFields(targetItem);
  const selectedTemplateName = targetTemplateId
    ? fullTemplate?.name || templates.find(t => t.id === targetTemplateId)?.name
    : undefined;

  useEffect(() => {
    if (!isDetoxTestControlsEnabled || !fullTemplate || targetCategoryId) {
      return;
    }

    const firstCategory = fullTemplate.categories?.[0];
    const firstItem = firstCategory ? getAllItems(firstCategory)[0] : null;
    if (firstCategory && firstItem) {
      setTargetCategoryId(firstCategory.id);
      setTargetItemId(firstItem.id);
    }
  }, [fullTemplate, targetCategoryId]);

  useEffect(() => {
    if (targetTemplateId) {
      const fetchTemplateDetails = async () => {
        setLoadingTemplate(true);
        try {
          const templateDetails = await reviewService.getTemplateDetails(targetTemplateId, { preferFresh: true });
          setFullTemplate(templateDetails as ReviewTemplate);
        } catch (error) {
          console.error('[ShareItemModal] Error loading template:', error);
          Alert.alert('Error', 'Failed to load template details. Please try again.');
        } finally {
          setLoadingTemplate(false);
        }
      };

      fetchTemplateDetails();
    } else {
      setFullTemplate(null);
    }
  }, [targetTemplateId]);

  useEffect(() => {
    if (!targetItemId || !targetItem) {
      setQuestionMappings([]);
      setQuestionsToCopy(new Set());
      return;
    }

    const nextMappingState = buildInitialShareMappingState({
      sourceItemName: sourceItem.name,
      sourceFields,
      targetFields,
      isNewItem
    });

    setQuestionMappings(nextMappingState.questionMappings);
    setQuestionsToCopy(nextMappingState.questionsToCopy);
  }, [targetItemId, targetItem, sourceItem, isNewItem]);

  useEffect(() => {
    setCreateItemMode(false);
    setNewItemName(sourceItem.name);
    setNewItemDescription(sourceItem.description || '');
  }, [targetCategoryId, sourceItem]);

  const handleClose = () => {
    setTargetTemplateId(null);
    setTargetCategoryId(null);
    setTargetItemId(null);
    setQuestionMappings([]);
    setQuestionsToCopy(new Set());
    setSourceMappingMenuId(null);
    setTargetMappingMenuId(null);
    setCreateItemMode(false);
    setNewItemName('');
    setNewItemDescription('');
    onDismiss();
  };

  const handleSelectFirstTarget = () => {
    if (!firstAvailableTemplate) {
      return;
    }

    setTargetTemplateId(firstAvailableTemplate.id);
    setTargetCategoryId(null);
    setTargetItemId(null);
  };

  const handleSelectTemplate = (template: ReviewTemplate) => {
    setTargetTemplateId(template.id);
    setTargetCategoryId(null);
    setTargetItemId(null);
    setTemplateMenuVisible(false);
  };

  const handleSelectCategory = (category: ReviewCategory) => {
    setTargetCategoryId(category.id);
    setTargetItemId(null);
    setCategoryMenuVisible(false);
  };

  const handleSelectItem = (item: ReviewItem) => {
    setTargetItemId(item.id);
    setCreateItemMode(false);
    setItemMenuVisible(false);
  };

  const handleStartCreateItem = () => {
    setCreateItemMode(true);
  };

  const handleCancelCreateItem = () => {
    setCreateItemMode(false);
    setNewItemName(sourceItem.name);
    setNewItemDescription(sourceItem.description || '');
  };

  const handleUpdateMapping = (mappingId: string, field: 'sourceId' | 'targetId', value: string) => {
    setQuestionMappings(prev => prev.map(mapping => {
      if (mapping.id !== mappingId) return mapping;

      return updateQuestionMapping({
        mapping,
        mappingField: field,
        value,
        sourceItemName: sourceItem.name,
        sourceFields,
        targetFields
      });
    }));
  };

  const handleRemoveMapping = (mappingId: string) => {
    setQuestionMappings(prev => prev.filter(m => m.id !== mappingId));
  };

  const handleAddMapping = () => {
    const result = buildNewQuestionMapping({
      sourceItemName: sourceItem.name,
      sourceFields,
      targetFields,
      existingMappings: questionMappings,
      questionsToCopy
    });

    if (result.error === 'all-mapped') {
      Alert.alert('Info', 'All compatible questions are already mapped or selected for copying.');
      return;
    }

    if (result.error === 'no-compatible-targets') {
      Alert.alert('Info', 'Selected question has no compatible targets.');
      return;
    }

    setQuestionMappings(prev => [...prev, result.mapping]);
  };

  const handleToggleCopy = (sourceQuestionId: string) => {
    const newToCopy = new Set(questionsToCopy);

    if (newToCopy.has(sourceQuestionId)) {
      newToCopy.delete(sourceQuestionId);
    } else {
      newToCopy.add(sourceQuestionId);
    }

    setQuestionsToCopy(newToCopy);
  };

  const handleToggleIncludeRoom = () => setIncludeRoom(prev => !prev);
  const handleToggleIncludePhotos = () => setIncludePhotos(prev => !prev);
  const handleToggleIncludeFloor = () => setIncludeFloor(prev => !prev);

  const handleSubmit = async () => {
    if (!targetItemId || !targetItem) {
      Alert.alert('Error', 'Please select a target item');
      return;
    }

    const totalSelected = questionMappings.length + questionsToCopy.size;
    if (totalSelected === 0 && !includeRoom && !includePhotos && !includeFloor) {
      Alert.alert('Error', 'Please select at least one question or metadata option to share');
      return;
    }

    setLoading(true);
    try {
      const { mappings } = await createShareLink({
        service: reviewService,
        sourceItem,
        targetItem,
        sourceFields,
        questionMappings,
        questionsToCopy,
        includeRoom,
        includePhotos,
        includeFloor
      });

      Alert.alert(
        'Success',
        `Linked "${sourceItem.name}" with "${targetItem.name}".\n${mappings.length} questions will be shared.`
      );

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to link items:', error);
      Alert.alert(
        'Error',
        getShareActionErrorMessage(error, 'Failed to link items. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDetoxCreateFirstLink = async () => {
    const firstCategory = fullTemplate?.categories?.[0];
    const firstItem = firstCategory ? getAllItems(firstCategory)[0] : null;

    if (!firstItem) {
      Alert.alert('Error', 'No target item is available for sharing');
      return;
    }

    setLoading(true);
    try {
      await createMetadataOnlyShareLink({
        service: reviewService,
        sourceItem,
        targetItem: firstItem
      });

      Alert.alert('Success', `Linked "${sourceItem.name}" with "${firstItem.name}".`);
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to link items:', error);
      Alert.alert(
        'Error',
        getShareActionErrorMessage(error, 'Failed to link items. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewItem = async () => {
    if (!newItemName.trim() || !targetCategoryId) {
      Alert.alert('Error', 'Please enter an item name');
      return;
    }

    setCreatingItem(true);
    try {
      const newItem = await createShareTargetItem({
        service: reviewService,
        categoryId: targetCategoryId,
        name: newItemName,
        description: newItemDescription
      });

      setTargetItemId(newItem.id);
      setCreateItemMode(false);
      setNewItemName('');
      setNewItemDescription('');

      if (targetTemplateId) {
        const templateDetails = await reviewService.getTemplateDetails(targetTemplateId, { preferFresh: true });
        setFullTemplate(templateDetails as ReviewTemplate);
      }

      Alert.alert('Success', `Created new item "${newItem.name}"`);
    } catch (error) {
      console.error('[ShareItemModal] Error creating item:', error);
      Alert.alert('Error', getShareActionErrorMessage(error, 'Failed to create item'));
    } finally {
      setCreatingItem(false);
    }
  };

  const getCompatibleTargets = (sourceField: QuestionField): QuestionField[] => {
    return getCompatibleQuestionTargets(sourceField, targetFields);
  };

  const canCreateLink = Boolean(
    targetItemId
      && (questionMappings.length > 0 || questionsToCopy.size > 0 || includeRoom || includePhotos || includeFloor)
  );

  return {
    availableTemplates,
    canCreateLink,
    categoryMenuVisible,
    createItemMode,
    creatingItem,
    detoxControlsEnabled: isDetoxTestControlsEnabled,
    firstAvailableTemplate,
    fullTemplate,
    getCompatibleTargets,
    handleAddMapping,
    handleCancelCreateItem,
    handleClose,
    handleCreateNewItem,
    handleDetoxCreateFirstLink,
    handleRemoveMapping,
    handleSelectCategory,
    handleSelectFirstTarget,
    handleSelectItem,
    handleSelectTemplate,
    handleStartCreateItem,
    handleSubmit,
    handleToggleCopy,
    handleToggleIncludeFloor,
    handleToggleIncludePhotos,
    handleToggleIncludeRoom,
    handleUpdateMapping,
    includeFloor,
    includePhotos,
    includeRoom,
    isNewItem,
    itemMenuVisible,
    loading,
    loadingTemplate,
    newItemDescription,
    newItemName,
    questionMappings,
    questionsToCopy,
    selectedCategory,
    selectedTemplateName,
    setCategoryMenuVisible,
    setItemMenuVisible,
    setNewItemDescription,
    setNewItemName,
    setSourceMappingMenuId,
    setTargetMappingMenuId,
    setTemplateMenuVisible,
    sourceFields,
    sourceMappingMenuId,
    targetCategories,
    targetCategoryId,
    targetItem,
    targetItemId,
    targetItems,
    targetMappingMenuId,
    targetTemplateId,
    targetFields,
    templateMenuVisible
  };
};
